import { inject } from '@angular/core';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { DateFormats, formatDateToString, USER_DATA_TOKEN, UserDataProvider, UserModel } from '@core';
import { CommonUserPenaltyDto, GetUserPenaltiesResponse, PenaltyType } from '@core-http';
import { PenaltiesService } from '@fair-play/infra';
import { DashboardMessageId, DashboardMessageQueryResult } from '../../../models/dashboard-message.query-result';
import { DashboardMessagesStorage } from '../../../../infrastructure/storage/dashboard-messages.storage';

export class DashboardRedCardMessageQueryHandler {
  private readonly penaltiesService: PenaltiesService = inject(PenaltiesService);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly storage: DashboardMessagesStorage = inject(DashboardMessagesStorage);

  message(): Observable<DashboardMessageQueryResult | null> {
    const activePenalty$: Observable<CommonUserPenaltyDto | null> = this.userDataProvider.userData$.pipe(
      switchMap((user: UserModel) => this.penaltiesService.getPenalties({ userId: user.userId })),
      map((response: GetUserPenaltiesResponse) => response.activePenalty ?? null)
    );

    return combineLatest([activePenalty$, this.storage.dismissedRedCardIds$]).pipe(
      map(([penalty, dismissedIds]: [CommonUserPenaltyDto | null, ReadonlySet<string>]) =>
        this.toMessage(penalty, dismissedIds)
      )
    );
  }

  private toMessage(
    penalty: CommonUserPenaltyDto | null,
    dismissedIds: ReadonlySet<string>
  ): DashboardMessageQueryResult | null {
    if (!penalty || penalty.penaltyType !== PenaltyType.RED) {
      return null;
    }

    if (dismissedIds.has(penalty.userPenaltyId)) {
      return null;
    }

    const expiration: string = formatDateToString(penalty.expirationDate, DateFormats.DayMonthYearDot);

    return {
      id: DashboardMessageId.RedCard,
      priority: 3,
      title: 'Dostałeś czerwoną kartkę!',
      subtitle: `Twoja kartka będzie wyświetlana organizatorom gierek do: ${expiration}`,
      hasIcon: true,
      clickable: false,
      action: 'close',
      dismissToken: penalty.userPenaltyId
    };
  }
}
